import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import FitnessCenterRounded from '@mui/icons-material/FitnessCenterRounded';
import LaptopChromebookRounded from '@mui/icons-material/LaptopChromebookRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import { useNavigate } from 'react-router';
import { useCitySuggestions, useLabels, type SearchParams } from '../api';
import { parsePlace, writeSearch } from '../searchUrl';

const ONLINE = '__online__';

interface PlaceOption {
  key: string;
  label: string;
  city?: string;
  uf?: string;
  count?: number;
}

interface SearchPanelProps {
  initial?: SearchParams;
  /** Big hero version (home) or compact bar (results page). */
  size?: 'hero' | 'compact';
  onSearch?: (params: SearchParams) => void;
}

/** "Where?" + "What for?" + search. Cities come from where professionals actually are. */
export function SearchPanel({ initial, size = 'hero', onSearch }: SearchPanelProps) {
  const navigate = useNavigate();
  const labels = useLabels();
  const initialPlace =
    initial?.mode === 'online' ? 'Online' : [initial?.city, initial?.uf].filter(Boolean).join(', ');
  const [place, setPlace] = useState(initialPlace);
  const [specialty, setSpecialty] = useState(initial?.specialty ?? '');
  const cities = useCitySuggestions(place.includes(',') ? '' : place);

  const options: PlaceOption[] = [
    { key: ONLINE, label: 'Online, de qualquer lugar' },
    ...(cities.data ?? []).map((c) => ({
      key: `${c.city}|${c.uf}`,
      label: `${c.city}, ${c.uf}`,
      city: c.city,
      uf: c.uf,
      count: c.count,
    })),
  ];

  const submit = (chosen?: PlaceOption | string | null) => {
    const base: SearchParams = { ...initial, page: undefined, city: undefined, uf: undefined };
    let next: SearchParams;
    const value = chosen ?? place;
    if (typeof value !== 'string' && value?.key === ONLINE) {
      next = { ...base, mode: 'online' };
    } else if (typeof value !== 'string' && value?.city) {
      next = {
        ...base,
        city: value.city,
        uf: value.uf,
        mode: base.mode === 'online' ? undefined : base.mode,
      };
    } else {
      const text = typeof value === 'string' ? value : '';
      const online = /^online/i.test(text.trim());
      next = online
        ? { ...base, mode: 'online' }
        : { ...base, ...parsePlace(text), mode: base.mode === 'online' ? undefined : base.mode };
    }
    next.specialty = specialty || undefined;
    if (onSearch) onSearch(next);
    else void navigate(writeSearch(next));
  };

  const hero = size === 'hero';
  return (
    <Paper
      component="form"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      variant={hero ? 'elevation' : 'outlined'}
      sx={(theme) => ({
        p: hero ? { xs: 1.5, sm: 2 } : 1.25,
        borderRadius: hero ? `${Number(theme.shape.borderRadius) * 2.2}px` : undefined,
        ...(hero && {
          boxShadow: `0 30px 80px -40px ${theme.alpha(theme.vars.palette.primary.dark, 0.55)}`,
          border: `1px solid ${theme.vars.palette.divider}`,
        }),
      })}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
        <Autocomplete
          freeSolo
          options={options}
          filterOptions={(list) => list}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          inputValue={place}
          onInputChange={(_, value) => setPlace(value)}
          onChange={(_, value) => {
            if (value && typeof value !== 'string') {
              setPlace(value.key === ONLINE ? 'Online' : value.label);
            }
          }}
          renderOption={({ key, ...props }, option) => (
            <Box component="li" key={key} {...props} sx={{ gap: 1.25 }}>
              {option.key === ONLINE ? (
                <LaptopChromebookRounded fontSize="small" color="primary" />
              ) : (
                <PlaceRounded fontSize="small" color="action" />
              )}
              <Box sx={{ flex: 1 }}>{option.label}</Box>
              {option.count != null && (
                <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                  {option.count} {option.count === 1 ? 'personal' : 'personais'}
                </Box>
              )}
            </Box>
          )}
          sx={{ flex: 1.4 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Onde você quer treinar?"
              placeholder="Cidade, bairro ou online"
              size={hero ? 'medium' : 'small'}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlaceRounded color="primary" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
        <TextField
          select
          label="Seu objetivo"
          value={specialty}
          onChange={(event) => setSpecialty(event.target.value)}
          size={hero ? 'medium' : 'small'}
          sx={{ flex: 1 }}
          slotProps={{
            select: { displayEmpty: true },
            inputLabel: { shrink: true },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <FitnessCenterRounded color="primary" />
                </InputAdornment>
              ),
            },
          }}
        >
          <MenuItem value="">Qualquer objetivo</MenuItem>
          {labels.meta?.specialties.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          type="submit"
          variant="contained"
          size={hero ? 'large' : 'medium'}
          startIcon={<SearchRounded />}
          sx={{ px: hero ? 4 : 2.5, minHeight: hero ? 56 : 40, flexShrink: 0 }}
        >
          Buscar
        </Button>
      </Stack>
    </Paper>
  );
}
